import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { formatISO, parseISO } from "date-fns";
import {
  AdvanceRateGroupCell,
  RateFormulaCellComponent,
} from "features/credit-facility/borrowing-base/InvestorPage";
import { getCreditFacilityBorrowingBaseGroupListQueryKey } from "gen/endpoints/credit-facility-borrowing-base-group/credit-facility-borrowing-base-group";
import {
  getCreditFacilityBorrowingBaseInvestorEntitiesTableDataRetrieveQueryKey,
  getCreditFacilityBorrowingBaseVersionRetrieveQueryKey,
  useCreditFacilityBorrowingBaseInvestorEntitiesTableDataRetrieveSuspense,
  useCreditFacilityBorrowingBaseVersionEdit,
  useCreditFacilityBorrowingBaseVersionRetrieveSuspense,
} from "gen/endpoints/credit-facility-borrowing-base-investors/credit-facility-borrowing-base-investors";
import { BooleanTableCellValue } from "gen/model/booleanTableCellValue";
import { BorrowingBaseVersionUpsertDataRequest } from "gen/model/borrowingBaseVersionUpsertDataRequest";
import { PercentTableCellValue } from "gen/model/percentTableCellValue";
import { TextTableCellValue } from "gen/model/textTableCellValue";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import {
  FormFooter,
  FormFooterActions,
} from "shared/components/form/FormFooter";
import { DateInput } from "shared/components/input/date/DateInput";
import { FormNumberInput } from "shared/components/input/text/NumberInput";
import { FormTextArea } from "shared/components/input/text/TextArea";
import { FormTextInput } from "shared/components/input/text/TextInput";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBadges } from "shared/components/table/box/TableBoxBadges";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { defaultDataTableRenderCellValue } from "shared/components/table/data/DataTableCellValueRenderer";
import { RenderCellValueFn } from "shared/components/table/data/DataTableTypes";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { nowAsIsoDate } from "shared/utils/DateUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";

export const CreditFacilitySettingsBorrowingBaseEdit: React.FC = () => {
  const { t } = useTranslation(["creditFacility"]);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { fundFamilyId, creditFacilityId, borrowingBaseGroupId } =
    useRequiredStringParams();

  const { data: borrowingBaseGroupData } =
    useCreditFacilityBorrowingBaseVersionRetrieveSuspense(
      fundFamilyId,
      creditFacilityId,
      borrowingBaseGroupId,
      {},
      {
        query: {
          refetchOnMount: "always",
        },
      },
    );

  const [versionDate, setVersionDate] = useState(nowAsIsoDate());

  const { mutateAsync: updateGroupVersion } =
    useCreditFacilityBorrowingBaseVersionEdit();

  const form = useForm<BorrowingBaseVersionUpsertDataRequest>({
    defaultValues: {
      ...borrowingBaseGroupData,
      version_date: versionDate,
    },
  });

  const { data: tableData, isLoading } =
    useCreditFacilityBorrowingBaseInvestorEntitiesTableDataRetrieveSuspense(
      fundFamilyId,
      creditFacilityId,
      borrowingBaseGroupId,
      {},
      {
        query: {
          refetchOnMount: "always",
        },
      },
    );

  // this is all of the entities and their selected state
  const [rowSelectionState] = useState(
    tableData.rows.reduce<{
      [id: string]: boolean;
    }>(
      (state, row) => ({
        ...state,
        [row.id]: (row.cells.is_selected.value as BooleanTableCellValue).value,
      }),
      {},
    ),
  );

  // this is only the selected entities
  const [selectedState, setSelectedState] = useState(
    Object.keys(rowSelectionState).reduce(
      (selectedState, id) => ({
        ...selectedState,
        ...(rowSelectionState[id] ? { [id]: true } : {}),
      }),
      {},
    ),
  );

  const renderCellValue = useCallback<RenderCellValueFn>(
    (props, fieldDisabled, params) => {
      const { columnContext, cell, row } = props;
      if (columnContext.columnId == "rate") {
        return (
          <RateFormulaCellComponent
            rate={(cell.value as PercentTableCellValue).value}
          />
        );
      }
      if (columnContext.columnId == "advance_rate_group") {
        return (
          <AdvanceRateGroupCell
            currentGroupName={(cell.value as TextTableCellValue).text}
            groupName={borrowingBaseGroupData.name}
            currentRowId={row.id}
            selectedIds={Object.keys(selectedState)}
          />
        );
      }
      return defaultDataTableRenderCellValue(props, fieldDisabled, params);
    },
    [borrowingBaseGroupData.name, selectedState],
  );

  const settingsRoute = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .setup({})
    .creditFacilities({});

  const goBack = () =>
    navigate(
      settingsRoute
        .creditFacility({ creditFacilityId })
        .settingsBorrowingBase({}).$,
    );

  return (
    <PageContent>
      <PageHeader
        title={t("creditFacility:borrowingBase.update.editPage.title", {
          groupName: borrowingBaseGroupData.name,
        })}
        helperText={t("creditFacility:borrowingBase.update.investors.info", {
          groupName: borrowingBaseGroupData.name,
        })}
      />
      <PageBody>
        <Form
          form={form}
          onSubmit={async (data) =>
            await updateGroupVersion({
              fundFamilyId,
              creditFacilityId,
              borrowingBaseGroupId,
              data: {
                ...data,
                investor_entity_ids: Object.keys(selectedState),
                version_date: versionDate,
                individual_concentration_limit:
                  data.individual_concentration_limit || null,
                aggregate_concentration_limit:
                  data.aggregate_concentration_limit || null,
              },
            })
          }
          onSuccess={async () => {
            await invalidateQueryKeys(
              queryClient,
              getCreditFacilityBorrowingBaseVersionRetrieveQueryKey(
                fundFamilyId,
                creditFacilityId,
                borrowingBaseGroupData.id,
              ),
              getCreditFacilityBorrowingBaseGroupListQueryKey(
                fundFamilyId,
                creditFacilityId,
              ),
              getCreditFacilityBorrowingBaseInvestorEntitiesTableDataRetrieveQueryKey(
                fundFamilyId,
                creditFacilityId,
                borrowingBaseGroupId,
              ),
            );
            goBack();
          }}
        >
          <VStack alignItems="stretch" gap="16px">
            <Card p="40px">
              <CardHeader>
                <Text textStyle="overline" textColor="gray.500">
                  {t("creditFacility:settings.advanceRateGroupVersion")}
                </Text>
              </CardHeader>
              <CardBody width="lg">
                <DateInput
                  title="Version Date"
                  value={parseISO(versionDate)}
                  onChangeValue={(value) =>
                    value
                      ? setVersionDate(
                          formatISO(value, { representation: "date" }),
                        )
                      : undefined
                  }
                />
                <FormTextArea
                  name="reason_for_change"
                  title={t("creditFacility:settings.edit.reasonForChange")}
                />
              </CardBody>
            </Card>
            <Card p="40px">
              <CardBody width="lg">
                <FormTextInput
                  showMessages={true}
                  name="name"
                  title={t(
                    "creditFacility:borrowingBase.update.advanceRateGroupName",
                  )}
                />
                <FormNumberInput
                  showMessages={true}
                  name="rate"
                  valueType="percent"
                  title={t(
                    "creditFacility:borrowingBase.update.unfundedCommitmentAvailableForBorrowing",
                  )}
                />
                <FormNumberInput
                  showMessages={true}
                  name="individual_concentration_limit"
                  valueType="percent"
                  unsetWhenEmpty={true}
                  title={t(
                    "creditFacility:borrowingBase.concentrationLimits.individualConcentrationLimit",
                  )}
                  helpText={t(
                    "creditFacility:borrowingBase.concentrationLimits.individualConcentrationLimitHelpText",
                  )}
                />
                <FormNumberInput
                  showMessages={true}
                  unsetWhenEmpty={true}
                  helpText={t(
                    "creditFacility:borrowingBase.concentrationLimits.aggregateConcentrationLimitHelpText",
                  )}
                  name="aggregate_concentration_limit"
                  valueType="percent"
                  title={t(
                    "creditFacility:borrowingBase.concentrationLimits.aggregateConcentrationLimit",
                  )}
                />
              </CardBody>
            </Card>
            <TableBox isFullWidth={true} isFullHeight={true}>
              <TableBoxHeader>
                <TableBoxTitle>
                  {t("creditFacility:borrowingBase.update.investorEntities")}
                </TableBoxTitle>
                <TableBoxBadges>
                  <Badge>
                    {t(
                      "creditFacility:borrowingBase.update.investors.investorEntitiesSelected",
                      { count: Object.keys(selectedState).length },
                    )}
                  </Badge>
                </TableBoxBadges>
                <DataTableBoxActions />
              </TableBoxHeader>
              <TableBoxBody>
                <DataTable
                  data={tableData}
                  isLoading={isLoading}
                  rowSelection={rowSelectionState}
                  onRowSelectionChange={setSelectedState}
                  renderCellValue={renderCellValue}
                />
              </TableBoxBody>
            </TableBox>
          </VStack>
          <FormFooter>
            <FormFooterActions>
              <ButtonOrder
                dismiss={<FormCancelButton onClick={goBack} />}
                confirm={
                  <FormSubmitButton>
                    {t(
                      "creditFacility:borrowingBase.update.investors.saveSelection",
                    )}
                  </FormSubmitButton>
                }
              />
            </FormFooterActions>
          </FormFooter>
        </Form>
      </PageBody>
    </PageContent>
  );
};
